import React, { useState } from "react";
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Box,
  Grid,
  TextField,
  Button,
  Skeleton,
  Alert,
  Autocomplete,
  CircularProgress,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  AccountCircle as AccountCircleIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import { TablaHead } from "src/components/_dashboard/tablas";
import Scrollbar from "src/components/Scrollbar";
import {
  useLazyObtenerRutsAlternativosQuery,
  useEliminarRutAlternativoMutation,
  useToggleRutAlternativoMutation,
} from "src/services/conciliacionApi";
import { clientesApi } from "src/services/clientesApi";
import { formatearRutDisplay } from "src/utils/conciliacionHelpers";
import { formatRut, RutFormat } from "@fdograph/rut-utilities";
import { useSnackbar } from "src/components/snackbar";
import ModalRutAlternativo from "./ModalRutAlternativo";

const useStyles = makeStyles((theme) => ({
  searchBox: {
    marginBottom: theme.spacing(3),
  },
  emptyState: {
    padding: theme.spacing(8),
    textAlign: "center",
  },
  clienteCard: {
    marginBottom: theme.spacing(3),
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: theme.spacing(1),
    overflow: "hidden",
  },
  clienteCardHeader: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  clienteCardBody: {
    padding: theme.spacing(2),
  },
  clienteInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  badge: {
    marginLeft: theme.spacing(1),
  },
}));

const TABLE_HEAD = [
  { id: "rut_cliente", label: "RUT Cliente", alignRight: false },
  { id: "nombre_cliente", label: "Nombre Cliente", alignRight: false },
  { id: "rut_alternativo", label: "RUT Alternativo", alignRight: false },
  { id: "descripcion", label: "Descripción", alignRight: false },
  { id: "estado", label: "Estado", alignRight: false },
  { id: "fecha_creacion", label: "Fecha Creación", alignRight: false },
  { id: "acciones", label: "Acciones", alignRight: true },
];

export default function RutsAlternativosTable() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  // Estados
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // RTK Query - Obtener lista de clientes
  const { data: clientes = [], isLoading: cargandoClientes } =
    clientesApi.useGetClientesQuery();

  // RTK Query - Buscar RUTs alternativos
  const [buscarRuts, { data, isLoading, isFetching }] =
    useLazyObtenerRutsAlternativosQuery();

  const [eliminarRut, { isLoading: isDeleting }] =
    useEliminarRutAlternativoMutation();
  const [toggleRut, { isLoading: isToggling }] =
    useToggleRutAlternativoMutation();

  // Datos
  const cliente = data?.cliente || {};
  const rutsAlternativos = data?.data || [];

  // Handlers
  const handleToggleEstado = async (id, estadoActual) => {
    try {
      await toggleRut({ id, activo: !estadoActual }).unwrap();
      enqueueSnackbar(
        `RUT alternativo ${
          !estadoActual ? "activado" : "desactivado"
        } exitosamente`,
        { variant: "success" },
      );
      // Refrescar datos
      if (clienteSeleccionado) {
        buscarRuts(clienteSeleccionado.rut);
      }
    } catch (error) {
      enqueueSnackbar(error?.data?.message || "Error al cambiar estado", {
        variant: "error",
      });
    }
  };

  const handleEliminar = async (id) => {
    try {
      await eliminarRut(id).unwrap();
      enqueueSnackbar("RUT alternativo eliminado exitosamente", {
        variant: "success",
      });
      // Refrescar datos
      if (clienteSeleccionado) {
        buscarRuts(clienteSeleccionado.rut);
      }
    } catch (error) {
      enqueueSnackbar(error?.data?.message || "Error al eliminar", {
        variant: "error",
      });
    }
  };

  const handleModalClose = (refrescar = false) => {
    setModalOpen(false);
    if (refrescar && clienteSeleccionado) {
      buscarRuts(clienteSeleccionado.rut);
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return "-";
    return new Date(fecha).toLocaleDateString("es-CL");
  };

  // ==================== FUNCIONES HELPER ====================

  /**
   * Formatea la opción del Autocomplete: "NOMBRE CLIENTE (RUT)"
   */
  const getOptionLabel = (option) => {
    if (!option || !option.rut) return "";
    const rutFormateado = formatRut(option.rut, RutFormat.DOTS_DASH);
    return `${option.nombre} (${rutFormateado})`;
  };

  /**
   * Compara opciones del Autocomplete
   */
  const isOptionEqualToValue = (option, value) => {
    return option.rut === value.rut;
  };

  /**
   * Filtra opciones: permite buscar por NOMBRE o RUT
   */
  const filterOptions = (options, { inputValue }) => {
    if (!inputValue) return options;

    const normalizado = inputValue.toLowerCase().trim();

    return options.filter((cliente) => {
      // Buscar en nombre
      const nombre = cliente.nombre?.toLowerCase() || "";

      // Buscar en RUT (sin formato)
      const rutSinFormato = cliente.rut?.replace(/[.-]/g, "") || "";
      const busquedaSinFormato = normalizado.replace(/[.-]/g, "");

      return (
        nombre.includes(normalizado) ||
        rutSinFormato.includes(busquedaSinFormato)
      );
    });
  };

  // ==================== HANDLERS ====================

  /**
   * Handler cuando se selecciona un cliente en el Autocomplete
   */
  const handleClienteSeleccion = (event, nuevoCliente) => {
    if (!nuevoCliente) {
      // Usuario limpió la selección (click en X)
      setClienteSeleccionado(null);
      return;
    }

    // Guardar cliente seleccionado
    setClienteSeleccionado(nuevoCliente);

    // Buscar automáticamente sus RUTs alternativos
    buscarRuts(nuevoCliente.rut);

    enqueueSnackbar(`Cliente seleccionado: ${nuevoCliente.nombre}`, {
      variant: "info",
    });
  };

  /**
   * Handler para abrir modal con cliente pre-llenado
   */
  const handleAgregarNuevo = () => {
    if (!clienteSeleccionado) {
      enqueueSnackbar("Primero selecciona un cliente", { variant: "warning" });
      return;
    }
    setModalOpen(true);
  };

  return (
    <Card>
      {/* BÚSQUEDA CON AUTOCOMPLETE */}
      <Box p={3} className={classes.searchBox}>
        <Grid container spacing={2} alignItems="center">
          {/* AUTOCOMPLETE DE CLIENTES */}
          <Grid item xs={12} md={8}>
            <Autocomplete
              fullWidth
              disableClearable={false}
              value={clienteSeleccionado}
              onChange={handleClienteSeleccion}
              options={clientes}
              getOptionLabel={getOptionLabel}
              isOptionEqualToValue={isOptionEqualToValue}
              filterOptions={filterOptions}
              loading={cargandoClientes}
              disabled={cargandoClientes}
              noOptionsText="No se encontraron clientes"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buscar Cliente"
                  placeholder="Escribe el nombre o RUT del cliente..."
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <SearchIcon
                        style={{ marginLeft: 8, marginRight: 4, color: "#999" }}
                      />
                    ),
                    endAdornment: (
                      <>
                        {cargandoClientes ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* BOTÓN AGREGAR NUEVO */}
          <Grid item xs={12} md={4}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={<AddIcon />}
              onClick={handleAgregarNuevo}
              disabled={!clienteSeleccionado}
            >
              Agregar RUT Alternativo
            </Button>
          </Grid>
        </Grid>

        {/* ========== CARD DE CLIENTE SELECCIONADO ========== */}
        {clienteSeleccionado && (
          <Box pt={3} px={3} pb={3}>
            <Card className={classes.clienteCard}>
              {/* HEADER */}
              <Box className={classes.clienteCardHeader}>
                <Box display="flex" alignItems="center">
                  <AccountCircleIcon style={{ fontSize: 28, marginRight: 8 }} />
                  <Typography variant="h6">Cliente Seleccionado</Typography>
                </Box>
                <Chip
                  label={`${rutsAlternativos.length} RUT${
                    rutsAlternativos.length !== 1 ? "s" : ""
                  } Alternativo${rutsAlternativos.length !== 1 ? "s" : ""}`}
                  size="small"
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    color: "#fff",
                    fontWeight: 600,
                  }}
                />
              </Box>

              {/* BODY */}
              <Box className={classes.clienteCardBody}>
                <Grid container spacing={2}>
                  {/* NOMBRE */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="textSecondary">
                      Nombre
                    </Typography>
                    <Typography variant="body1" style={{ fontWeight: 500 }}>
                      {clienteSeleccionado.nombre || "Sin nombre"}
                    </Typography>
                  </Grid>

                  {/* RUT */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="textSecondary">
                      RUT
                    </Typography>
                    <Typography variant="body1" style={{ fontWeight: 500 }}>
                      {formatearRutDisplay(clienteSeleccionado.rut)}
                    </Typography>
                  </Grid>

                  {/* MENSAJE INFORMATIVO */}
                  {rutsAlternativos.length === 0 &&
                    !isLoading &&
                    !isFetching && (
                      <Grid item xs={12}>
                        <Alert severity="info" style={{ marginTop: 8 }}>
                          <Typography variant="body2">
                            Este cliente no tiene RUTs alternativos registrados.
                            Agrega uno para mejorar la conciliación automática.
                          </Typography>
                        </Alert>
                      </Grid>
                    )}

                  {rutsAlternativos.length > 0 && (
                    <Grid item xs={12}>
                      <Alert severity="success" style={{ marginTop: 8 }}>
                        <Typography variant="body2">
                          {`Mostrando ${rutsAlternativos.length} RUT${
                            rutsAlternativos.length !== 1 ? "s" : ""
                          } alternativo${
                            rutsAlternativos.length !== 1 ? "s" : ""
                          } registrado${
                            rutsAlternativos.length !== 1 ? "s" : ""
                          }.`}
                        </Typography>
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Card>
          </Box>
        )}
      </Box>

      {/* TABLA */}
      {clienteSeleccionado && (
        <>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <TablaHead headLabel={TABLE_HEAD} />
                <TableBody>
                  {/* LOADING */}
                  {(isLoading || isFetching) && (
                    <>
                      {[...Array(3)].map((_, index) => (
                        <TableRow key={`skeleton-${index}`}>
                          {TABLE_HEAD.map((column) => (
                            <TableCell key={column.id}>
                              <Skeleton variant="text" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </>
                  )}

                  {/* DATA */}
                  {!isLoading &&
                    !isFetching &&
                    rutsAlternativos.length > 0 &&
                    rutsAlternativos.map((rut) => (
                      <TableRow key={rut.id} hover>
                        {/* RUT CLIENTE */}
                        <TableCell>
                          <Typography variant="body2">
                            {formatearRutDisplay(rut.ref_cliente)}
                          </Typography>
                        </TableCell>

                        {/* NOMBRE CLIENTE */}
                        <TableCell>
                          <Typography variant="body2">
                            {cliente.nombre || "-"}
                          </Typography>
                        </TableCell>

                        {/* RUT ALTERNATIVO */}
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {formatearRutDisplay(rut.rut_alternativo)}
                          </Typography>
                        </TableCell>

                        {/* DESCRIPCIÓN */}
                        <TableCell>
                          <Typography variant="body2">
                            {rut.descripcion || "-"}
                          </Typography>
                        </TableCell>

                        {/* ESTADO */}
                        <TableCell>
                          <Chip
                            label={rut.activo ? "Activo" : "Inactivo"}
                            size="small"
                            color={rut.activo ? "success" : "default"}
                            icon={
                              rut.activo ? <CheckCircleIcon /> : <CancelIcon />
                            }
                          />
                        </TableCell>

                        {/* FECHA CREACIÓN */}
                        <TableCell>
                          <Typography variant="body2">
                            {formatearFecha(rut.created_at)}
                          </Typography>
                        </TableCell>

                        {/* ACCIONES */}
                        <TableCell align="right">
                          <Box
                            display="flex"
                            justifyContent="flex-end"
                            gap={0.5}
                          >
                            <Tooltip
                              title={
                                rut.activo ? "Desactivar RUT" : "Activar RUT"
                              }
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleToggleEstado(rut.id, rut.activo)
                                }
                                disabled={isToggling}
                              >
                                {rut.activo ? (
                                  <CancelIcon color="warning" />
                                ) : (
                                  <CheckCircleIcon color="success" />
                                )}
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Eliminar">
                              <IconButton
                                size="small"
                                onClick={() => handleEliminar(rut.id)}
                                disabled={isDeleting}
                              >
                                <DeleteIcon color="error" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}

                  {/* EMPTY STATE */}
                  {!isLoading &&
                    !isFetching &&
                    rutsAlternativos.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Box className={classes.emptyState}>
                            <Typography variant="h6" color="textSecondary">
                              No hay RUTs alternativos registrados
                            </Typography>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              mt={1}
                            >
                              Haz clic en "Agregar Nuevo" para crear una
                              relación
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
        </>
      )}

      {/* EMPTY STATE INICIAL */}
      {!clienteSeleccionado && (
        <Box className={classes.emptyState}>
          <SearchIcon style={{ fontSize: 64, color: "#ccc" }} />
          <Typography variant="h6" color="textSecondary" mt={2}>
            Busca un cliente para gestionar sus RUTs alternativos
          </Typography>
          <Typography variant="body2" color="textSecondary" mt={1}>
            Ingresa el RUT del cliente y haz clic en "Buscar"
          </Typography>
        </Box>
      )}

      {/* MODAL CREAR/EDITAR */}
      <ModalRutAlternativo
        open={modalOpen}
        onClose={handleModalClose}
        clienteSeleccionado={clienteSeleccionado}
      />
    </Card>
  );
}
